// src/ServiceRequestForm.js
import React, { useEffect, useMemo, useState } from "react";
import logo from "./dhos-logo.png";

const API_BASE = process.env.REACT_APP_API_BASE;

// ---------- Helpers ----------
function qsValue(name) {
  var p = new URLSearchParams(window.location.search);
  return p.get(name) || "";
}

function prettyTime(epochSeconds) {
  if (!epochSeconds) return "-";
  try {
    return new Date(epochSeconds * 1000).toLocaleString([], {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch (e) {
    return String(epochSeconds);
  }
}

async function publicFetch(path, opts) {
  opts = opts || {};
  var method = opts.method || "GET";
  var token = opts.token;
  var body = opts.body;

  var headers = { "Content-Type": "application/json" };
  if (token) headers["X-Access-Token"] = token;

  var url = String(API_BASE || "") + path;

  var res = await fetch(url, {
    method: method,
    headers: headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  var text = await res.text();
  var data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch (e) {
    data = { raw: text };
  }

  if (!res.ok) {
    var msg = (data && (data.error || data.message)) || ("HTTP " + res.status);
    var err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}

function isEmailLike(v) {
  var s = String(v || "").trim();
  if (!s) return false;
  return s.indexOf("@") > 0 && s.indexOf(".") > 0;
}

// ---------- Small UI Components ----------
function Card(props) {
  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e2e8f0",
        borderRadius: 14,
        padding: 18,
        boxShadow: "0 6px 18px rgba(0,0,0,0.04)",
      }}
    >
      {props.title ? (
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 14 }}>
          <div style={{ fontWeight: 900, color: "#0f172a" }}>{props.title}</div>
          {props.right || null}
        </div>
      ) : null}
      {props.children}
    </div>
  );
}

function TextField(props) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 12, fontWeight: 800, color: "#334155" }}>
        {props.label} {props.required ? <span style={{ color: "#dc2626" }}>*</span> : null}
      </label>
      <input
        value={props.value}
        onChange={props.onChange}
        placeholder={props.placeholder || ""}
        disabled={!!props.disabled}
        type={props.type || "text"}
        style={{
          padding: "10px 12px",
          borderRadius: 10,
          border: "1px solid #e2e8f0",
          outline: "none",
          fontSize: 14,
          background: props.disabled ? "#f8fafc" : "white",
        }}
      />
      {props.hint ? <div style={{ fontSize: 12, color: "#64748b" }}>{props.hint}</div> : null}
    </div>
  );
}

function TextAreaField(props) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 12, fontWeight: 800, color: "#334155" }}>
        {props.label} {props.required ? <span style={{ color: "#dc2626" }}>*</span> : null}
      </label>
      <textarea
        value={props.value}
        onChange={props.onChange}
        placeholder={props.placeholder || ""}
        disabled={!!props.disabled}
        rows={props.rows || 3}
        style={{
          padding: "10px 12px",
          borderRadius: 10,
          border: "1px solid #e2e8f0",
          outline: "none",
          fontSize: 14,
          resize: "vertical",
          background: props.disabled ? "#f8fafc" : "white",
        }}
      />
      {props.hint ? <div style={{ fontSize: 12, color: "#64748b" }}>{props.hint}</div> : null}
    </div>
  );
}

function RadioPill({ value, selected, onSelect, label, disabled }) {
  var active = selected === value;
  return (
    <button
      type="button"
      onClick={() => (!disabled ? onSelect(value) : null)}
      disabled={!!disabled}
      style={{
        padding: "10px 12px",
        borderRadius: 999,
        border: "1px solid " + (active ? "#0f172a" : "#e2e8f0"),
        background: active ? "#0f172a" : "white",
        color: active ? "white" : "#0f172a",
        fontWeight: 800,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {label}
    </button>
  );
}

function PrimaryButton(props) {
  return (
    <button
      type={props.type || "button"}
      onClick={props.onClick}
      disabled={!!props.disabled}
      style={{
        padding: "10px 14px",
        borderRadius: 12,
        border: "1px solid #0f172a",
        background: props.disabled ? "#94a3b8" : "#0f172a",
        color: "white",
        fontWeight: 900,
        cursor: props.disabled ? "not-allowed" : "pointer",
        ...(props.style || {}),
      }}
      title={props.title || ""}
    >
      {props.children}
    </button>
  );
}

function SecondaryButton(props) {
  return (
    <button
      type={props.type || "button"}
      onClick={props.onClick}
      disabled={!!props.disabled}
      style={{
        padding: "10px 14px",
        borderRadius: 12,
        border: "1px solid #e2e8f0",
        background: props.disabled ? "#f1f5f9" : "white",
        color: "#0f172a",
        fontWeight: 900,
        cursor: props.disabled ? "not-allowed" : "pointer",
        ...(props.style || {}),
      }}
      title={props.title || ""}
    >
      {props.children}
    </button>
  );
}

// ---------- Main ----------
export default function ServiceRequestForm() {
  var incidentId = qsValue("incidentId") || qsValue("id");
  var token = qsValue("token");

  var [loading, setLoading] = useState(false);
  var [submitting, setSubmitting] = useState(false);
  var [error, setError] = useState("");

  // ✅ Success screen state (like CustomerForm)
  var [submitSuccess, setSubmitSuccess] = useState(false);
  var [submittedAtDate, setSubmittedAtDate] = useState(null);

  var [caseSummary, setCaseSummary] = useState(null);

  // Form state
  var [senderName, setSenderName] = useState("");
  var [senderEmail, setSenderEmail] = useState("");
  var [pickupAddress, setPickupAddress] = useState("");
  var [packageDimensions, setPackageDimensions] = useState(""); // e.g. 30 x 20 x 15 cm
  var [packageWeightKg, setPackageWeightKg] = useState("");    // e.g. 2.5

  

  var [shipmentOption, setShipmentOption] = useState("REGULAR"); // REGULAR | IMMEDIATE
  var [receiverName, setReceiverName] = useState("");
  var [receiverEmail, setReceiverEmail] = useState("");
  var [deliveryAddress, setDeliveryAddress] = useState("");

  var [logisticsCompany, setLogisticsCompany] = useState("");
  var [logisticsAccountNumber, setLogisticsAccountNumber] = useState("");

  var disableIfBusy = loading || submitting;

  function hydrateFromGetPayload(payload) {
    var d = (payload && payload.serviceRequestData) || {};
    var sending = d.sending || {};
    var receiving = d.receiving || {};
    var logistics = (receiving && receiving.logistics) || {};

    setSenderName(String(sending.senderName || ""));
    setSenderEmail(String(sending.senderEmail || ""));
    setPickupAddress(String(sending.pickupAddress || ""));
    setPackageDimensions(String(sending.packageDimensions || ""));
    setPackageWeightKg(String(sending.packageWeightKg || ""));


    var opt = String(receiving.shipmentOption || "REGULAR").toUpperCase();
    if (opt !== "REGULAR" && opt !== "IMMEDIATE") opt = "REGULAR";
    setShipmentOption(opt);

    setReceiverName(String(receiving.receiverName || ""));
    setReceiverEmail(String(receiving.receiverEmail || ""));
    setDeliveryAddress(String(receiving.deliveryAddress || ""));

    setLogisticsCompany(String(logistics.company || ""));
    setLogisticsAccountNumber(String(logistics.accountNumber || ""));
  }

async function load() {
  setError("");

  if (!API_BASE) {
    setError("Missing REACT_APP_API_BASE");
    return;
  }
  if (!incidentId) {
    setError("Missing incidentId in URL (expected ?incidentId=...)");
    return;
  }
  if (!token) {
    setError("Missing token in URL (expected ?token=...)");
    return;
  }

  setLoading(true);
  try {
    var data = await publicFetch("/public/service-request/" + encodeURIComponent(incidentId), {
      method: "GET",
      token: token,
    });

    setCaseSummary(data);
    hydrateFromGetPayload(data);

    // ✅ show "Last submitted" info, but DO NOT auto-show success screen
    var submittedAtEpoch = data && data.serviceRequestSubmittedAt;
    setSubmittedAtDate(submittedAtEpoch ? new Date(Number(submittedAtEpoch) * 1000) : null);

    // IMPORTANT: do not setSubmitSuccess() here
  } catch (e) {
    setError(e.message || "Failed to load");
  } finally {
    setLoading(false);
  }
}

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  var submittedAtEpoch = caseSummary && caseSummary.serviceRequestSubmittedAt;

  var displayIncidentId =
    (caseSummary && (caseSummary.reportNo || (caseSummary.incidentData && caseSummary.incidentData.reportNo))) || incidentId;

  // ✅ Sending is mandatory for BOTH options
  var canSubmit = useMemo(() => {
    if (submitting || loading) return false;

    // Sending ALWAYS required
    if (!senderName.trim()) return false;
    if (!senderEmail.trim() || !isEmailLike(senderEmail)) return false;
    if (!pickupAddress.trim()) return false;
    if (!packageDimensions.trim()) return false;
    if (!String(packageWeightKg || "").trim()) return false;
    var w = Number(packageWeightKg);
    if (!isFinite(w) || w <= 0) return false;



    // Receiver ALWAYS required
    if (!receiverName.trim()) return false;
    if (!receiverEmail.trim() || !isEmailLike(receiverEmail)) return false;

    // IMMEDIATE requires extra fields
    if (shipmentOption === "IMMEDIATE") {
      if (!deliveryAddress.trim()) return false;
      if (!logisticsCompany.trim()) return false;
      if (!logisticsAccountNumber.trim()) return false;
    }

    return true;
  }, [
    submitting,
    loading,
    shipmentOption,
    senderName,
    senderEmail,
    pickupAddress,
    receiverName,
    receiverEmail,
    deliveryAddress,
    logisticsCompany,
    logisticsAccountNumber,
    packageDimensions,
    packageWeightKg,

  ]);

  async function submit() {
    setError("");

    if (!API_BASE) {
      setError("Missing REACT_APP_API_BASE");
      return;
    }
    if (!incidentId || !token) {
      setError("Missing incidentId/token in URL");
      return;
    }

    if (!canSubmit) {
      setError("Please complete all required fields.");
      return;
    }

    setSubmitting(true);
    try {
      var upperOpt = String(shipmentOption || "").toUpperCase();
      var regular = upperOpt === "REGULAR";

      var payload = {
        sending: {
          senderName: String(senderName || "").trim(),
          senderEmail: String(senderEmail || "").trim(),
          pickupAddress: String(pickupAddress || "").trim(),
          packageDimensions: String(packageDimensions || "").trim(),
          packageWeightKg: String(packageWeightKg || "").trim(),

        },
        receiving: {
          shipmentOption: upperOpt,
          receiverName: String(receiverName || "").trim(),
          receiverEmail: String(receiverEmail || "").trim(),
          deliveryAddress: regular ? "N/A" : String(deliveryAddress || "").trim(),
          logistics: {
            company: regular ? "N/A" : String(logisticsCompany || "").trim(),
            accountNumber: regular ? "N/A" : String(logisticsAccountNumber || "").trim(),
          },
        },
      };

      var resp = await publicFetch("/public/service-request/" + encodeURIComponent(incidentId) + "/submit", {
        method: "POST",
        token: token,
        body: payload,
      });

      // ✅ Success screen like Incident Report flow
      var epoch = (resp && resp.serviceRequestSubmittedAt) || _now();
      setSubmittedAtDate(new Date(Number(epoch) * 1000));
      setSubmitSuccess(true);
    } catch (e) {
      setError(e.message || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  }

  // helper (client-side)
  function _now() {
    return Math.floor(Date.now() / 1000);
  }

  // ✅ Success screen (also shown when reusing a submitted link)
  if (submitSuccess) {
    return (
      <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
        <div style={{ maxWidth: 980, margin: "0 auto", padding: "22px 16px 40px" }}>
          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <img src={logo} alt="DHOS" style={{ height: 34 }} />
            <div style={{ fontWeight: 900, color: "#0f172a", fontSize: 18 }}>Service Request Form</div>
          </div>

          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <div
              style={{
                background: "white",
                border: "1px solid #e2e8f0",
                borderRadius: 14,
                padding: 18,
                boxShadow: "0 6px 18px rgba(0,0,0,0.04)",
              }}
            >
              <h1 style={{ margin: 0, color: "#0f172a" }}>✅ Service Request Submitted</h1>
              <div style={{ marginTop: 10, color: "#475569", fontSize: 14 }}>
                Thank you. Your service request has been submitted successfully.
              </div>

              <div style={{ marginTop: 16, color: "#64748b", fontSize: 13 }}>
                {submittedAtDate ? "Submitted: " + submittedAtDate.toLocaleString() : null}
              </div>

              <div
                style={{
                  marginTop: 18,
                  padding: 12,
                  borderRadius: 12,
                  background: "#f8fafc",
                  color: "#475569",
                  fontSize: 13,
                }}
              >
                You can now close this window.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
      <div style={{ maxWidth: 980, margin: "0 auto", padding: "22px 16px 40px" }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <img src={logo} alt="DHOS" style={{ height: 34 }} />
          <div style={{ fontWeight: 900, color: "#0f172a", fontSize: 18 }}>Service Request Form</div>
        </div>

        <Card title={displayIncidentId ? "Incident ID: " + displayIncidentId : "Incident ID"}>
          {submittedAtEpoch ? (
            <div style={{ color: "#64748b", fontSize: 12, marginBottom: 12 }}>
              Last submitted: <b>{prettyTime(submittedAtEpoch)}</b>
            </div>
          ) : (
            <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 12 }}>Not submitted yet.</div>
          )}

          {loading ? <div style={{ padding: 12, color: "#64748b" }}>Loading…</div> : null}

          {error ? (
            <div style={{ marginBottom: 12, padding: 12, borderRadius: 12, background: "#fef2f2", color: "#991b1b", fontWeight: 800 }}>
              {error}
            </div>
          ) : null}

          {/* Sections */}
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {/* Sending */}
            <div>
              <div style={{ fontWeight: 900, color: "#0f172a", marginBottom: 6 }}>Sending Information</div>

              <div style={{ color: "#475569", fontSize: 13, marginBottom: 12, lineHeight: 1.45 }}>
                Please fill in the information for sending the item back to DHOS and wait for an email from DHOS Logistics to arrange for item pickup.
              </div>
              
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <TextField
                label="Sender Name"
                required={true}
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                disabled={disableIfBusy}
            />

            <TextField
                label="Sender Email"
                required={true}
                value={senderEmail}
                onChange={(e) => setSenderEmail(e.target.value)}
                disabled={disableIfBusy}
                placeholder="name@company.com"
            />

            <div style={{ gridColumn: "1 / -1" }}>
                <TextAreaField
                label="Pickup Address"
                required={true}
                value={pickupAddress}
                onChange={(e) => setPickupAddress(e.target.value)}
                disabled={disableIfBusy}
                rows={3}
                placeholder="Full pickup address"
                />
            </div>

            <TextField
                label="Package Dimensions (L x W x H)"
                required={true}
                value={packageDimensions}
                onChange={(e) => setPackageDimensions(e.target.value)}
                disabled={disableIfBusy}
                placeholder="e.g. 30 x 20 x 15 cm"
            />

            <TextField
                label="Weight (KG)"
                required={true}
                value={packageWeightKg}
                onChange={(e) => setPackageWeightKg(e.target.value)}
                disabled={disableIfBusy}
                placeholder="e.g. 2.5"
                type="number"
            />
            </div>

            </div>

            {/* Receiving */}
            <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 16 }}>
              <div style={{ fontWeight: 900, color: "#0f172a", marginBottom: 10 }}>Receiving Options</div>

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
                <RadioPill value="REGULAR" selected={shipmentOption} onSelect={setShipmentOption} label="DHOS Regular shipments" disabled={disableIfBusy} />
                <RadioPill
                  value="IMMEDIATE"
                  selected={shipmentOption}
                  onSelect={setShipmentOption}
                  label="Immediate shipment (Airfreight / courier)"
                  disabled={disableIfBusy}
                />
              </div>

              {shipmentOption === "IMMEDIATE" ? (
                <div style={{ marginBottom: 14, padding: 12, borderRadius: 12, background: "#fff7ed", border: "1px solid #fed7aa", color: "#9a3412", fontWeight: 800 }}>
                  Note: If Immediate shipment is selected, please provide the Affiliate’s logistics account details. The return shipment will be borne by the Affiliate, while the outbound shipment will be arranged by DHOS.
                </div>
              ) : (
                <div style={{ marginBottom: 14, padding: 12, borderRadius: 12, background: "#f1f5f9", border: "1px solid #e2e8f0", color: "#475569", fontWeight: 800 }}>
                  Note: For DHOS Regular shipments, Delivery Address / Logistics will be recorded as <b>N/A</b>.
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                <TextField label="Receiver Name" required value={receiverName} onChange={(e) => setReceiverName(e.target.value)} disabled={disableIfBusy} />
                <TextField
                  label="Receiver Email"
                  required
                  value={receiverEmail}
                  onChange={(e) => setReceiverEmail(e.target.value)}
                  disabled={disableIfBusy}
                  placeholder="name@company.com"
                />

                {shipmentOption === "IMMEDIATE" ? (
                  <>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <TextAreaField
                        label="Delivery Address"
                        required
                        value={deliveryAddress}
                        onChange={(e) => setDeliveryAddress(e.target.value)}
                        disabled={disableIfBusy}
                        rows={3}
                        placeholder="Full delivery address"
                      />
                    </div>

                    <TextField
                      label="Logistics Company"
                      required
                      value={logisticsCompany}
                      onChange={(e) => setLogisticsCompany(e.target.value)}
                      disabled={disableIfBusy}
                      placeholder="e.g. DHL / FedEx / UPS"
                    />
                    <TextField
                      label="Logistics Account number"
                      required
                      value={logisticsAccountNumber}
                      onChange={(e) => setLogisticsAccountNumber(e.target.value)}
                      disabled={disableIfBusy}
                      placeholder="Account number"
                    />
                  </>
                ) : (
                  <>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <TextAreaField label="Delivery Address" required={false} value={"N/A"} onChange={() => {}} disabled={true} rows={2} />
                    </div>

                    <TextField label="Logistics Company" required={false} value={"N/A"} onChange={() => {}} disabled={true} />
                    <TextField label="Logistics Account number" required={false} value={"N/A"} onChange={() => {}} disabled={true} />
                  </>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* ✅ Submit button at bottom, centered */}
        <div style={{ display: "flex", justifyContent: "center", marginTop: 24 }}>
          <PrimaryButton
            disabled={!canSubmit || submitting}
            onClick={submit}
            title={canSubmit ? "Submit service request" : "Complete required fields"}
            style={{ minWidth: 220 }}
          >
            {submitting ? "Submitting..." : "Submit Service Request"}
          </PrimaryButton>
        </div>

        <div style={{ marginTop: 14, color: "#94a3b8", fontSize: 12 }}>Tip: Please keep this page open until you see the submitted screen.</div>
      </div>
    </div>
  );
}
